import { useState, useEffect, useCallback } from "react";
import { ScrollFade } from "@/components/ScrollFade";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { LinkedInPagesPanel, PageSelect } from "@/components/LinkedInPagesPanel";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Linkedin, Save, AlertTriangle } from "lucide-react";

const STATUT = { actif: ["Actif", "bg-emerald-50 text-emerald-700"], en_creation: ["En création", "bg-amber-50 text-amber-700"] };

const allCaps = (s) => !!s && s === s.toLocaleUpperCase("fr") && s !== s.toLocaleLowerCase("fr");
const FILTRES = {
  ville: { label: "sans ville des posts", testid: "groups-empty-city-filter", test: (g) => !g.ville_posts },
  maj: { label: "au nom tout en majuscules", testid: "groups-uppercase-filter", test: (g) => allCaps(g.nom_affiche) },
};

const FieldEdit = ({ g, champ, value, width, onSaved }) => {
  const [val, setVal] = useState(value);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.put(`/admin/conferences/groupes/${encodeURIComponent(g.id)}/${champ}`, { valeur: val });
      toast.success(r.conferences
        ? `Enregistré : ${r.conferences} conférence(s) non publiée(s) mise(s) à jour, ${r.textes_regeneres} texte(s) régénéré(s), ${r.textes_a_revoir} texte(s) retouché(s) à revoir.${r.visuels_a_revalider ? " Visuels à revalider." : " Visuels inchangés."}`
        : "Enregistré");
      onSaved(r.groupe);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex gap-2">
      <input value={val} onChange={(e) => setVal(e.target.value)} data-testid={`group-${champ}-input-${g.id}`} className={`${width} border border-slate-300 rounded-sm px-2 py-1.5 text-sm`} />
      {val.trim() !== value && <button onClick={save} disabled={busy || !val.trim()} data-testid={`group-${champ}-save-${g.id}`} className="inline-flex items-center gap-1 bg-ink text-white text-xs font-semibold px-3 py-1.5 rounded-sm disabled:opacity-40"><Save className="h-3.5 w-3.5" /> Enregistrer</button>}
    </div>
  );
};

const ETAPES = {
  jeton: "Étape 1 sur 2 en échec : échange du code d'autorisation contre le jeton. Aucun jeton n'a été enregistré.",
  pages: "Étape 2 sur 2 en échec : le jeton est obtenu et enregistré, mais la liste des pages administrées a été refusée.",
};

const OAuthError = ({ e, onClose }) => (
  <div role="alert" className="mb-6 border border-red-200 bg-red-50 text-red-800 rounded-sm p-4 text-sm" data-testid="pages-oauth-error">
    <p className="font-semibold">Connexion LinkedIn (pages APOLLO) impossible</p>
    <p className="mt-1" data-testid="pages-oauth-error-step">{ETAPES[e.etape] || "Étape inconnue."}</p>
    <p className="mt-1 text-xs" data-testid="pages-oauth-error-detail">Réponse de LinkedIn : HTTP {e.http || "sans réponse"}{e.detail ? `, ${e.detail}` : ""}</p>
    <button onClick={onClose} className="mt-2 text-xs font-semibold underline" data-testid="pages-oauth-error-close">Fermer</button>
  </div>
);

const GroupPage = ({ g, st, onSaved }) => {
  if (!st?.compte) return <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Linkedin className="h-3.5 w-3.5" /> LinkedIn (pages APOLLO) pas encore connecté</span>;
  const save = async (v) => {
    try { await api.put(`/admin/conferences/groupes/${encodeURIComponent(g.id)}/page-linkedin`, { page_urn: v }); toast.success(v ? "Page du groupe associée" : "Page du groupe retirée"); onSaved({ ...g, page_urn: v || null }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return <PageSelect value={g.page_urn} pages={st.pages} exclude={[st.config.test_urn, st.config.france_urn]} onChange={save} testid={`group-page-select-${g.id}`} />;
};

const GroupRow = ({ g, st, onSaved }) => {
  const [label, cls] = STATUT[g.statut] || [g.statut || "Inconnu", "bg-slate-100 text-slate-600"];
  return (
    <tr className="border-t border-slate-100 align-middle" data-testid={`group-row-${g.id}`}>
      <td className="px-4 py-3"><p className="font-semibold" data-testid={`group-page-name-${g.id}`}>{g.page_nom}</p><p className="text-xs text-slate-400">Nom API : {g.nom}</p></td>
      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`} data-testid={`group-status-${g.id}`}>{label}</span></td>
      <td className="px-4 py-3"><FieldEdit key={g.nom_affiche} g={g} champ="nom" value={g.nom_affiche} width="w-52" onSaved={onSaved} /></td>
      <td className="px-4 py-3 text-slate-500">{g.ville_api || <span className="text-slate-300">Non communiquée</span>}</td>
      <td className="px-4 py-3">
        <FieldEdit key={g.ville_posts} g={g} champ="ville" value={g.ville_posts} width="w-44" onSaved={onSaved} />
        {!g.ville_posts && <p className="text-xs text-amber-700 mt-1" data-testid={`group-city-empty-${g.id}`}>Aucune ville : pas de ville dans l'invitation ni de hashtag de ville.</p>}
      </td>
      <td className="px-4 py-3" data-testid={`group-page-status-${g.id}`}><GroupPage g={g} st={st} onSaved={onSaved} /></td>
    </tr>
  );
};

export default function AdminGroupPages() {
  const { user } = useAuth();
  const [groupes, setGroupes] = useState(null);
  const [filter, setFilter] = useState(null);
  const [st, setSt] = useState(null);
  const [oauthErr, setOauthErr] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api.get("/admin/conferences/groupes").then((r) => setGroupes(r.data.groupes)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  const loadPages = useCallback(() => api.get("/admin/conferences/pages").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { if (user?.role === "admin") { load(); loadPages(); } }, [user, load, loadPages]);
  useEffect(() => {
    const r = params.get("linkedin");
    if (!r) return;
    const msgs = { ok: () => toast.success("LinkedIn (pages APOLLO) connecté"), annule: () => toast.info("Connexion LinkedIn annulée") };
    if (r === "erreur") setOauthErr({ etape: params.get("etape"), http: params.get("http"), detail: params.get("detail") });
    msgs[r]?.();
    setParams({}, { replace: true });
  }, [params, setParams]);
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  const onSaved = (g) => setGroupes((all) => all.map((x) => (x.id === g.id ? g : x)));
  const all = groupes || [];
  const rows = all.filter((g) => !filter || FILTRES[filter].test(g));
  return (
    <div className="p-8 lg:p-12" data-testid="admin-group-pages">
      <Link to="/admin/conferences" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-coral-dark mb-6"><ArrowLeft className="h-4 w-4" /> Conférences APOLLO</Link>
      <h1 className="font-serif text-4xl text-slate-900 mb-2">Pages LinkedIn des groupes</h1>
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">Le nom affiché sert dans l'annonce du post et l'aperçu LinkedIn ; la ville des posts dans l'invitation, le hashtag et le visuel. Un changement régénère le texte des conférences non publiées du groupe (un texte retouché à la main passe en « Texte à revoir »). Un changement de ville fait aussi revalider chaque visuel.</p>
      {oauthErr && <OAuthError e={oauthErr} onClose={() => setOauthErr(null)} />}
      {st && <LinkedInPagesPanel st={st} groupPages={all.map((g) => g.page_urn).filter(Boolean)} onReload={loadPages} />}
      {groupes && (
        <div className="mb-6 flex flex-wrap gap-2">
          {Object.entries(FILTRES).map(([k, f]) => {
            const n = all.filter(f.test).length;
            const on = filter === k;
            return (
              <button key={k} onClick={() => setFilter(on ? null : k)} disabled={!n && !on} data-testid={f.testid}
                className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border ${on ? "bg-amber-100 border-amber-300 text-amber-900" : n ? "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                <AlertTriangle className="h-4 w-4" />
                {n} groupe{n > 1 ? "s" : ""} {f.label}{on ? " · afficher tous les groupes" : ""}
              </button>
            );
          })}
        </div>
      )}
      <ScrollFade className="bg-white border border-slate-200 rounded-sm">
        <table className="w-full text-sm" data-testid="group-pages-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
            <tr><th className="px-4 py-3">Page</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Nom affiché</th><th className="px-4 py-3">Ville API</th><th className="px-4 py-3">Ville des posts</th><th className="px-4 py-3">Page LinkedIn du groupe</th></tr>
          </thead>
          <tbody>
            {groupes?.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aucun groupe reçu de l'API pour l'instant.</td></tr>}
            {rows.map((g) => <GroupRow key={g.id} g={g} st={st} onSaved={onSaved} />)}
          </tbody>
        </table>
      </ScrollFade>
    </div>
  );
}
